// Measurement source fusion: for each quantity, the most reliable fresh source wins.
// Power: power meter > connected trainer > virtual power (speed sensor + classic trainer curve).
// Any value outside physical bounds is discarded: it never reaches the display, the activity or a leaderboard.
import { virtualPower, type TrainerCurve } from '@gruvetto/core';
import type { Reading } from './gatt.js';

export type SourceId = 'meter' | 'trainer' | 'csc';
/** Where power comes from: measured, estimated from wheel speed, or nothing (keyboard simulation). */
export type PowerOrigin = 'meter' | 'trainer' | 'virtual' | null;

/** Last value received for each quantity, timestamped (ms). */
export type Latest = Partial<Record<keyof Reading, { v: number; at: number }>>;

export const FRESH_MS = 3000;
const BOUNDS: Record<keyof Reading, [number, number]> = { power: [0, 2500], cadence: [0, 250], speed: [0, 100], hr: [25, 250] };

/** Records a frame into a source's state, skipping missing or outlier values. */
export function record(into: Latest, d: Reading, now: number) {
  for (const k of Object.keys(d) as (keyof Reading)[]) {
    const v = d[k], [lo, hi] = BOUNDS[k];
    if (v == null || !Number.isFinite(v)) continue;
    if (k === 'power' && v < 0) { into[k] = { v: 0, at: now }; continue; } // some trainers send -1 when stopped
    if (v < lo || v > hi) continue;
    into[k] = { v, at: now };
  }
}

/** `cadenceKnown` false: no sensor measures cadence (e.g. a power meter without a crank counter). */
export interface Fused { power: number; cadence: number; cadenceKnown: boolean; speed: number; origin: PowerOrigin }

export function fuse(src: Partial<Record<SourceId, Latest>>, curve: TrainerCurve | null, now: number): Fused {
  const fresh = (id: SourceId, k: keyof Reading) => { const x = src[id]?.[k]; return x && now - x.at <= FRESH_MS ? x.v : null; };
  let power: number | null, origin: PowerOrigin = null;
  if ((power = fresh('meter', 'power')) != null) origin = 'meter';
  else if ((power = fresh('trainer', 'power')) != null) origin = 'trainer';
  else if (curve) {
    const s = fresh('csc', 'speed') ?? fresh('meter', 'speed');
    if (s != null) { power = virtualPower(curve, s); origin = 'virtual'; }
  }
  const cadence = fresh('meter', 'cadence') ?? fresh('csc', 'cadence') ?? fresh('trainer', 'cadence');
  const speed = fresh('trainer', 'speed') ?? fresh('csc', 'speed') ?? fresh('meter', 'speed') ?? 0;
  return { power: power ?? 0, cadence: cadence ?? 0, cadenceKnown: cadence != null, speed, origin };
}

/** Cadence used to detect a stop (effort governor, ramp test): without a cadence sensor, rely on power. */
export const pedalingCadence = (f: Pick<Fused, 'power' | 'cadence' | 'cadenceKnown'>): number => (f.cadenceKnown ? f.cadence : f.power > 20 ? 90 : 0);
